/* @flow */
import React from 'react';
import { observable } from 'mobx';
import { observer } from 'mobx-react';
import { translate } from 'react-i18next';

//----------------------------------------------------------------------//
// MATERIAL UI                                                          //
//----------------------------------------------------------------------//

import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormHelperText from '@material-ui/core/FormHelperText';
import IconButton from '@material-ui/core/IconButton';
import Input from '@material-ui/core/Input';
import InputAdornment from '@material-ui/core/InputAdornment';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import Radio from '@material-ui/core/Radio';

//----------------------------------------------------------------------//
//----------------------------------------------------------------------//

import { apiService } from 'services/ServiceRegistry';
import { filterSearchList } from '../../../../utils/';

import ActionDrawerBlock from 'views/ActionDrawer/components/ActionDrawerBlock/ActionDrawerBlock';

import styles from './ElectroCheckList.module.scss';

type TypeProps = {
  field: Object,
  heading: string,
  divider: boolean,
  enableSearch: boolean,
  type: string,
  primaryLiteral: Function,
  secondaryLiteral: Function,
  setItemDisabled: Function,
};

/**
 * ElectroCheckList
 *
 * @prop {Object} field
 * @prop {String}
 *
 * @prop {Array<Object>} options
 * @prop {String} heading
 * @prop {String} type
 * @prop {Boolean} divider
 * @prop {Boolean} enableSearch
 * @prop {Array<String>} searchKeys
 * @prop {Array<String>} exclude
 * @prop {Function} primaryLiteral
 * @prop {Function} secondaryLiteral
 * @prop {Function} setItemDisabled
 * @prop {String} valueKey
 * @prop {Function} customResponse
 * @prop {Object} warningsField
 * @return {React.Fragment}
 */
class ElectroCheckList extends React.Component {
  LABEL_KEY_DEFAULT: string = 'name';

  VALUE_KEY_DEFAULT: string = 'id';

  TYPE_RADIO: string = 'radio';

  @observable loading: boolean = false;

  @observable search: string = '';

  @observable list: Array<Object> = [];

  get options(): Array<Object> {
    return this.search.length > 0
      ? filterSearchList(this.list, this.search, this.props.searchKeys || [this.LABEL_KEY_DEFAULT])
      : this.list;
  }

  get valueKey(): string {
    return this.props.valueKey ? this.props.valueKey : this.VALUE_KEY_DEFAULT;
  }

  get arrayLength(): number {
    return this.props.type === this.TYPE_RADIO ? this.options.length : this.list.length;
  }

  async loadOptions(endpoint: string): Promise<void> {
    this.loading = true;

    try {
      const response: * = await apiService.getResource(endpoint);

      this.handleOnLoad(response);
    } catch (err) {
      console.log(err);
    }

    this.loading = false;
  }

  filterExcludes = (arr: Array<Object>) => {
    const exclude: Array<string> = this.props.exclude;

    if (exclude) {
      return arr.filter((obj: Object) => !exclude.includes(obj[this.valueKey]));
    }

    return arr;
  };

  handleOnLoad = (response: *) => {
    const {
      field,
      type,
      customResponse,
    }: {
      field: Object,
      type: string,
      customResponse: Function,
    } = this.props;

    let arr: Array<Object> = [];

    if (customResponse) {
      arr = customResponse(response);
    } else {
      arr = response;
    }

    this.list = this.filterExcludes(arr);
  };

  handleOnSearch = (evt: SyntheticInputEvent<*>) => {
    const {
      field,
      type,
    }: {
      field: Object,
      type: string,
    } = this.props;

    this.search = evt.target.value;
  };

  handleOnCheckAllToggle = () => {
    const { field, setItemDisabled }: Object = this.props;

    if (this.options.length > 0) {
      if (field.value.length === this.options.length) {
        field.set([]);
      } else {
        field.set(
          this.options.map((item: Object) => {
            if (typeof setItemDisabled !== 'function') {
              return item[this.valueKey] ? item[this.valueKey] : null;
            } else if (!(setItemDisabled && setItemDisabled(item))) {
              return item[this.valueKey] ? item[this.valueKey] : null;
            }
          })
        );
      }
    }
  };

  handleOnItemClick = (item: Object) => {
    const {
      field,
      type,
      warningsField,
    }: {
      field: Object,
      type: string,
      warningsField: Object,
    } = this.props;

    const value: string = item[this.valueKey];

    if (type === this.TYPE_RADIO) {
      field.set(value);

      if (warningsField && item.warnings) {
        warningsField.set(item.warnings);
      }
    } else {
      const index: number = field.value.indexOf(value);
      const arr: Array<string> = [...field.value];

      if (index === -1) {
        arr.push(value);
      } else {
        arr.splice(index, 1);
      }

      field.set(arr);
    }
  };

  compareArrs = (arr1: Array<Object>, arr2: Array<Object>) => {
    if (arr1.length !== arr2.length) {
      return false;
    }

    arr1.sort();

    const tempArr: Array<Object> = [];

    for (let i: number = 0; i < arr2.length; i++) {
      tempArr[i] = arr2[i][this.valueKey];
    }

    tempArr.sort();

    for (let i: number = 0; i < arr1.length; i++) {
      if (arr1[i] !== tempArr[i]) {
        return false;
      }
    }

    return true;
  };

  componentDidMount() {
    const {
      field,
      endpoint,
      options,
      type,
    }: {
      field: Object,
      endpoint: string,
      options: Array<Object>,
      type: string,
    } = this.props;

    if (options) {
      this.list = options;

      if (type === this.TYPE_RADIO) {
        field.set(this.options.length > 0 ? this.options[0][this.valueKey] : '');
      }
    } else if (endpoint) {
      this.loadOptions(endpoint);
    }
  }

  render(): React.Fragment {
    const {
      field,
      heading,
      divider,
      enableSearch,
      type,
      primaryLiteral,
      secondaryLiteral,
      setItemDisabled,
      t,
    }: TypeProps = this.props;

    return (
      <React.Fragment>
        {enableSearch ? (
          <ActionDrawerBlock gutters={true}>
            <div className={styles['checklist-title']}>
              {heading && <h3>{heading}</h3>}
              {type !== this.TYPE_RADIO && (
                <FormControlLabel
                  label={t('general:actions.select_all')}
                  labelPlacement="start"
                  className={styles['select-all']}
                  control={
                    <Checkbox
                      disabled={this.arrayLength === 0}
                      checked={
                        //this.arrayLength > 0 &&
                        //field.value.length === this.arrayLength
                        this.options.length > 0 && this.compareArrs(field.value, this.list)
                      }
                      disableRipple={true}
                      tabIndex={-1}
                      onClick={this.handleOnCheckAllToggle}
                    />
                  }
                />
              )}
            </div>
            <Input
              className={styles['search-input']}
              value={this.search}
              placeholder={this.loading ? 'Loading...' : field.placeholder}
              disableUnderline={true}
              disabled={this.loading}
              fullWidth={true}
              autoComplete="on"
              onChange={this.handleOnSearch}
              startAdornment={
                <InputAdornment className={styles['column-input-adornment']} position="start">
                  <svg className={styles['icon-search']}>
                    <use xlinkHref="#icon-search" />
                  </svg>
                </InputAdornment>
              }
              endAdornment={
                <IconButton
                  className={styles['column-search-close']}
                  disabled={this.search.length < 1}
                  onClick={(evt: SyntheticInputEvent<*>) => {
                    this.search = '';
                  }}
                >
                  <svg className={styles['icon-close']}>
                    <use xlinkHref="#icon-close" />
                  </svg>
                </IconButton>
              }
            />
          </ActionDrawerBlock>
        ) : heading ? (
          <ActionDrawerBlock gutters={true} heading={heading} />
        ) : null}
        <ActionDrawerBlock flex={true} divider={divider && (!field.error || !field.showError)}>
          <div className={styles['list-scroll']}>
            <List disablePadding={true}>
              {this.options.map(
                (item: Object): ListItem => (
                  <ListItem
                    disabled={setItemDisabled ? setItemDisabled(item) : false}
                    key={item[this.valueKey]}
                    dense={true}
                    divider={false}
                    onClick={(evt: SyntheticInputEvent<*>) => {
                      setItemDisabled && setItemDisabled(item)
                        ? evt.preventDefault()
                        : this.handleOnItemClick(item);
                    }}
                  >
                    <ListItemText
                      primary={primaryLiteral ? primaryLiteral(item) : item[this.LABEL_KEY_DEFAULT]}
                      secondary={secondaryLiteral ? secondaryLiteral(item) : ''}
                    />
                    {type === this.TYPE_RADIO ? (
                      <Radio name={field.name} tabIndex={-1} />
                    ) : (
                      <Checkbox
                        checked={field.value.indexOf(item[this.valueKey]) > -1}
                        disableRipple={true}
                        tabIndex={-1}
                      />
                    )}
                  </ListItem>
                )
              )}
            </List>
          </div>
        </ActionDrawerBlock>
        {field.error && field.showError && (
          <ActionDrawerBlock gutters={true} divider={divider}>
            <FormHelperText error={true}>{field.error}</FormHelperText>
          </ActionDrawerBlock>
        )}
      </React.Fragment>
    );
  }
}

export default translate()(observer(ElectroCheckList));
